import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import _ from 'lodash';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useUpdateDiscernDocument } from '@hooks/api/discern';
import { useSnackbar } from '@hooks/useSnackbar';

import { formatName } from '@utils/format';
import { hasRole } from '@utils/role';

import type { RoleName } from 'generated/types';
import { useGetAllUsersLazyQuery } from 'generated/user.generated';

const messages = defineMessages({
  label: {
    id: 'discernAssignedUser.label',
    defaultMessage: 'Assigned User',
  },
  error: {
    id: 'discernAssignedUser.error',
    defaultMessage: 'Failed to assign user to document',
  },
  select: {
    id: 'GLOBAL.select',
    defaultMessage: 'Select',
  },
});

interface DiscernAssignedUserDropdownProps {
  name?: string | null;
  value?: string | null;
  discernDocumentId: string;
  groupingStateJson?: Record<string, any> | null;
  role: RoleName;
  account_id?: string | null;
  onUpdate?: () => void;
}

export default function DiscernAssignedUserDropdown({
  account_id,
  name,
  value,
  discernDocumentId,
  groupingStateJson,
  role,
}: DiscernAssignedUserDropdownProps) {
  const [labelId] = useState(_.uniqueId('discern-assigned-user-'));

  const [open, setOpen] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [getAllUsers, { data: usersData, loading: usersLoading }] =
    useGetAllUsersLazyQuery();
  const [updateDiscernDocument, updateLoading] = useUpdateDiscernDocument();
  const loading = usersLoading || updateLoading;

  const userOptions = (usersData?.getAllUsers ?? [])
    .filter((user) => hasRole(user, role))
    .sort((a, b) => a.first_name.localeCompare(b.first_name))
    .reduce<Record<string, string>>(
      (acc, { first_name, last_name, user_id }) => {
        acc[user_id] = formatName(first_name, last_name) ?? '-';
        return acc;
      },
      {}
    );

  const handleChange = async (user_id: string | null) => {
    try {
      if (value === user_id) {
        return;
      }

      // Update grouping_state_json with assigned user info
      const updatedGroupingState = {
        ...(groupingStateJson || {}),
        _metadata: {
          ...(groupingStateJson?._metadata || {}),
          assigned_user_id: user_id,
          assigned_user_name: user_id
            ? userOptions[user_id] || name || ''
            : null,
        },
      };

      await updateDiscernDocument({
        discern_document_id: discernDocumentId,
        grouping_state_json: updatedGroupingState,
      });
    } catch (e) {
      enqueueSnackbar({
        message: formatMessage(messages.error),
        variant: 'error',
      });
    }
  };

  const handleOpen = async (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setOpen(true);

    await getAllUsers({
      variables: {
        input: {
          account_id,
          roles: [role],
        },
      },
    });
  };

  const handleClose = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setOpen(false);
  };

  return (
    <Autocomplete
      disableClearable
      disabled={loading}
      id={labelId}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      isOptionEqualToValue={(option, val) => option === val}
      getOptionDisabled={(option) => option === value}
      getOptionLabel={(user_id) => {
        if (user_id && user_id in userOptions) {
          return userOptions[user_id];
        }
        return name ?? '';
      }}
      onChange={(event, val) => {
        event.stopPropagation();
        event.preventDefault();
        handleChange(val);
      }}
      options={Object.keys(userOptions)}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          disabled={loading}
          label={formatMessage(messages.label)}
          placeholder={formatMessage(messages.select)}
          onClick={(event) => {
            event.stopPropagation();
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              onClick: (e) => e.stopPropagation(),
              onFocus: (e) => e.stopPropagation(),
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            },
            inputLabel: { shrink: true },
          }}
          sx={({ palette, shadows }) => ({
            '& .MuiInputBase-root': {
              backgroundColor: palette.background.paper,
              boxShadow: shadows[1],
              fontSize: 14,
              ':hover': {
                cursor: 'pointer',
              },
            },
          })}
        />
      )}
      renderOption={(props, user_id) => {
        const userName =
          user_id in userOptions ? userOptions[user_id] : (name ?? '');
        return (
          <li {...props} key={`user-${user_id}`}>
            {userName}
          </li>
        );
      }}
      size="small"
      sx={{
        width: 'fit-content',
        minWidth: 160,
        '.MuiAutocomplete-input': {
          cursor: 'pointer',
          ':hover': {
            cursor: 'pointer',
          },
        },
      }}
      value={value ?? ''}
      ListboxProps={{ onClick: (e) => e.stopPropagation() }}
    />
  );
}
